import { useForm } from '@tanstack/react-form';
import { createContext, useContext } from 'react';
import { useSettings } from '../../../context/settings-context';
import {
  type CustomerGroupQuickSetupState,
  type CustomerGroupQuickSetupValidated,
  customerGroupQuickSetupSchema,
} from '../types/customer-group-quick-setup.schema';

function useDefaultQuickSetupState(): CustomerGroupQuickSetupState {
  const settings = useSettings();

  return {
    baseCurrency: settings.baseCurrency,
    name: '',
    assignedPriceList: '',
    paymentMethods: [],
    minOrderRequirements: [],
    minOrderValue: '',
    minOrderUnits: '',
  };
}

type CustomerGroupsQuickSetupFormProps<TError = unknown> = {
  onSubmit?: (payload: CustomerGroupQuickSetupValidated) => Promise<void> | void;
  onFailValidation?: () => Promise<void> | void;
  onSubmitError?: (error: TError) => Promise<void> | void;
};

export function useCustomerGroupsQuickSetupFormLogic<TError = unknown>({
  onSubmit,
  onFailValidation: onSubmitInvalid,
  onSubmitError: onError,
}: CustomerGroupsQuickSetupFormProps<TError>) {
  const defaultValues = useDefaultQuickSetupState();
  return useForm({
    defaultValues,
    validators: {
      onSubmitAsync: customerGroupQuickSetupSchema,
    },
    onSubmit: async ({ value }) => {
      const payload = await customerGroupQuickSetupSchema.parseAsync(value);
      try {
        await onSubmit?.(payload);
      } catch (err) {
        if (onError) {
          await onError(err as TError);
          return;
        }

        throw err;
      }
    },
    onSubmitInvalid,
  });
}

type CustomerGroupsQuickSetupFormApi = ReturnType<typeof useCustomerGroupsQuickSetupFormLogic>;

export const CustomerGroupsQuickSetupFormContext =
  createContext<CustomerGroupsQuickSetupFormApi | null>(null);

export function useCustomerGroupsQuickSetupForm() {
  const context = useContext(CustomerGroupsQuickSetupFormContext);

  if (!context) {
    throw new Error(
      'useCustomerGroupsQuickSetupForm must be used within a CustomerGroupsQuickSetupFormContext.Provider',
    );
  }

  return context;
}
